// src/components/assets/DisposeAssetModal.tsx
import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { TrackedAsset, DisposeAssetRequest, DisposalMethod, DISPOSAL_METHOD_LABELS } from '../../types/asset';

interface DisposeAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: DisposeAssetRequest) => void;
  asset: TrackedAsset;
}

const DisposeAssetModal: React.FC<DisposeAssetModalProps> = ({ isOpen, onClose, onConfirm, asset }) => {
  const [formData, setFormData] = useState({
    disposalMethod: '' as DisposalMethod | '',
    disposedTo: '',
    disposalAmount: '',
    disposalAuthorizedBy: '',
    disposalReference: '',
    disposalNotes: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmText, setConfirmText] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.disposalMethod) newErrors.disposalMethod = 'Disposal method is required';
    if (!formData.disposalAuthorizedBy.trim()) newErrors.disposalAuthorizedBy = 'Authorization is required';
    if (confirmText !== 'DISPOSE') newErrors.confirm = 'Type DISPOSE to confirm';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    const submitData: DisposeAssetRequest = {
      disposalMethod: formData.disposalMethod as DisposalMethod,
      disposalAuthorizedBy: formData.disposalAuthorizedBy,
      disposedTo: formData.disposedTo || undefined,
      disposalAmount: formData.disposalAmount ? parseFloat(formData.disposalAmount) : undefined,
      disposalReference: formData.disposalReference || undefined,
      disposalNotes: formData.disposalNotes || undefined
    };
    onConfirm(submitData);
  };

  const resetForm = () => {
    setFormData({ disposalMethod: '', disposedTo: '', disposalAmount: '', disposalAuthorizedBy: '', disposalReference: '', disposalNotes: '' });
    setErrors({});
    setConfirmText('');
  };

  const handleClose = () => { resetForm(); onClose(); };

  if (!isOpen) return null;

  const showRecipient = ['sold', 'donated', 'transferred', 'trade_in'].includes(formData.disposalMethod);
  const showAmount = ['sold', 'trade_in'].includes(formData.disposalMethod);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-red-50">
          <div className="flex items-center">
            <AlertTriangle className="w-6 h-6 text-red-600 mr-2" />
            <div>
              <h2 className="text-xl font-bold text-gray-800">Dispose Asset</h2>
              <p className="text-sm text-gray-500 font-mono">{asset.assetTag}</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-red-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
        </div>

        <div className="p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800">
              <strong>Warning:</strong> This action will mark the asset as disposed and remove it from active inventory. 
              This cannot be undone. The asset record will be retained for audit purposes.
            </p>
          </div>

          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Asset: <span className="font-medium text-gray-900">{asset.name}</span></p>
            <p className="text-sm text-gray-600">Category: <span className="font-medium text-gray-900">{asset.categoryName}</span></p>
            <p className="text-sm text-gray-600">Location: <span className="font-medium text-gray-900">{asset.location}</span></p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Disposal Method <span className="text-red-500">*</span></label>
              <select name="disposalMethod" value={formData.disposalMethod} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 ${errors.disposalMethod ? 'border-red-500' : 'border-gray-300'}`}>
                <option value="">Select method...</option>
                {Object.entries(DISPOSAL_METHOD_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              {errors.disposalMethod && <p className="text-red-500 text-xs mt-1">{errors.disposalMethod}</p>}
            </div>

            {showRecipient && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recipient</label>
                <input type="text" name="disposedTo" value={formData.disposedTo} onChange={handleInputChange} placeholder="Name or organization" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" />
              </div>
            )}

            {showAmount && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount Received</label>
                <input type="number" name="disposalAmount" value={formData.disposalAmount} onChange={handleInputChange} placeholder="0.00" step="0.01" min="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Authorized By <span className="text-red-500">*</span></label>
              <input type="text" name="disposalAuthorizedBy" value={formData.disposalAuthorizedBy} onChange={handleInputChange} placeholder="Name of authorizing official" className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 ${errors.disposalAuthorizedBy ? 'border-red-500' : 'border-gray-300'}`} />
              {errors.disposalAuthorizedBy && <p className="text-red-500 text-xs mt-1">{errors.disposalAuthorizedBy}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reference Document</label>
              <input type="text" name="disposalReference" value={formData.disposalReference} onChange={handleInputChange} placeholder="PO number, authorization doc, etc." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea name="disposalNotes" value={formData.disposalNotes} onChange={handleInputChange} rows={2} placeholder="Additional details..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" />
            </div>

            <div className="pt-4 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-1">Type DISPOSE to confirm</label>
              <input type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DISPOSE" className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 ${errors.confirm ? 'border-red-500' : 'border-gray-300'}`} />
              {errors.confirm && <p className="text-red-500 text-xs mt-1">{errors.confirm}</p>}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={handleClose} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">Cancel</button>
              <button type="submit" disabled={confirmText !== 'DISPOSE'} className={`px-4 py-2 text-white rounded-lg ${confirmText === 'DISPOSE' ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-400 cursor-not-allowed'}`}>Dispose Asset</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DisposeAssetModal;